import { baseApi } from '@/store/api/baseApi';
import type { OrderDetail, OrderListParams, PaginatedOrders, UpdateOrderStatusPayload } from '@/types/order';

function buildOrderListQuery(params: OrderListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'placedAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.customerId) searchParams.set('customerId', params.customerId);
  if (params.orderStatus) searchParams.set('orderStatus', params.orderStatus);
  if (params.paymentStatus) searchParams.set('paymentStatus', params.paymentStatus);
  if (params.paymentMethod) searchParams.set('paymentMethod', params.paymentMethod);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<PaginatedOrders, OrderListParams>({
      query: (params) => ({
        url: `/orders?${buildOrderListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildOrderListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildOrderListQuery(currentArg ?? {}) !== buildOrderListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Orders' as const, id })),
              { type: 'Orders', id: 'LIST' },
            ]
          : [{ type: 'Orders', id: 'LIST' }],
    }),
    getOrder: builder.query<OrderDetail, string>({
      query: (id) => `/orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Orders', id }],
    }),
    cancelOrder: builder.mutation<OrderDetail, string>({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Orders', id },
        { type: 'Orders', id: 'LIST' },
        { type: 'Inventory', id: 'LIST' },
        { type: 'Inventory', id: 'SUMMARY' },
      ],
    }),
    updateOrderStatus: builder.mutation<
      OrderDetail,
      { id: string; body: UpdateOrderStatusPayload }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}/status`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Orders', id },
        { type: 'Orders', id: 'LIST' },
        { type: 'Inventory', id: 'LIST' },
        { type: 'Inventory', id: 'SUMMARY' },
      ],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  useCancelOrderMutation,
  useUpdateOrderStatusMutation,
} = ordersApi;
